import React, { FC } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import HiddenAccessibilityText from '@app/components/inputs/HiddenAccessibilityText';
import { preventForwardTheseProps } from '@app/utils/styledUtils';

const ARC_COLOR = '#0284C7';
const NAVY = '#09215E';
const SLATE = '#707B99';

export interface NsknoxLoaderProps {
  /** `'mark'` renders just the spinner arc; `'wordmark'` renders the full nsknox logo with the animated "o". */
  variant?: 'mark' | 'wordmark';
  /** Rendered size in px. For `'mark'` it's the box width/height; for `'wordmark'` it's the logo height. */
  size?: number;
  /** Seconds per rotation. */
  speed?: number;
  color?: string;
  label?: string;
  className?: string;
}

const NsknoxLoader: FC<NsknoxLoaderProps> = ({
  variant = 'wordmark',
  size = variant === 'wordmark' ? 32 : 48,
  speed = 1.4,
  color = ARC_COLOR,
  label = 'Loading',
  className,
}) => (
  <Container role='status' aria-live='polite' aria-label={label} className={className} speed={speed}>
    {variant === 'wordmark' ? (
      <Svg height={size} viewBox='0 0 125 31' fill='none' aria-hidden='true' autoWidth>
        <g fill={SLATE}>
          <path
            transform='translate(0 9.84)'
            d='M.628 1.03h1.137l1.557 2.564C4.672 1.618 6.902.482 9.64.482c4.547 0 7.663 3.112 7.663 8.155v12.218H15.24V9.015c0-4.035-2.4-6.557-6.02-6.557-4 0-6.527 3.11-6.527 7.944v10.453H.628V1.029z'
          />
          <path d='M28.555 30.476c-.65 0-1.322-.08-2.01-.24a8.79 8.79 0 0 1-4.26-2.457 8.583 8.583 0 0 1-1.028-1.317l1.525-.965c.234.373.508.723.811 1.04a7.026 7.026 0 0 0 3.36 1.944c.516.12 2.09.487 3.8-.248 1.12-.481 2.064-1.435 2.463-2.486.295-.778.276-1.533-.06-2.243-.605-1.288-2.057-1.919-2.867-2.18-.595-.19-1.266-.323-1.975-.464-1.93-.384-4.116-.817-5.45-2.643-.802-1.102-1.043-2.57-.644-3.933.434-1.472 1.495-2.603 3.066-3.265 2.094-.883 4.805-.79 6.746.232a7.708 7.708 0 0 1 2.954 2.797l-1.537.944c-.398-.645-1.112-1.544-2.26-2.15-1.44-.758-3.627-.828-5.2-.166-.737.31-1.689.932-2.037 2.114-.239.818-.097 1.723.37 2.366.919 1.256 2.583 1.588 4.343 1.938.73.146 1.484.294 2.177.517 1.88.605 3.283 1.713 3.946 3.126.544 1.154.582 2.415.114 3.648-.575 1.515-1.86 2.824-3.436 3.501a7.29 7.29 0 0 1-2.91.59' />
        </g>
        <g fill={NAVY}>
          <path d='M39.804 0v30.694h4.01v-9.147h2.623l7.29 9.147h4.755l-8.687-10.976 7.432-8.85h-4.791l-6.06 7.492h-2.562V0z' />
          <path d='M61.853 10.827h2.526l1.43 2.523c1.264-1.935 3.454-3.069 6.234-3.069 4.59 0 7.622 3.195 7.622 8.24v12.171H76.17V19.108c0-3.49-2.107-5.673-5.265-5.673-3.494 0-5.558 2.73-5.558 6.976v10.281h-3.494V10.827z' />
          <path d='M120.086 10.867l-5.987 7.448-5.986-7.448h-4.475l8.225 10.232-7.715 9.6h4.47l5.48-6.819 5.481 6.82h4.47l-7.714-9.602 8.225-10.23z' />
        </g>
        <Arc cx={93.4} cy={20.5} r={8.4} fill='none' stroke={color} strokeWidth={3.3} strokeLinecap='round' pathLength={100} />
      </Svg>
    ) : (
      <Svg width={size} height={size} viewBox='0 0 32 32' fill='none' aria-hidden='true'>
        <Arc cx={16} cy={16} r={13} fill='none' stroke={color} strokeWidth={3} strokeLinecap='round' pathLength={100} />
      </Svg>
    )}
    <HiddenAccessibilityText>{label}</HiddenAccessibilityText>
  </Container>
);

export default NsknoxLoader;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1 100;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 71 100;
    stroke-dashoffset: -28;
  }
  100% {
    stroke-dasharray: 71 100;
    stroke-dashoffset: -98;
  }
`;

const Container = styled('span', { shouldForwardProp: preventForwardTheseProps('speed') })<{ speed: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  --nsknox-loader-speed: ${(p): string => `${p.speed}s`};
`;

const Svg = styled.svg<{ autoWidth?: boolean }>`
  display: block;
  ${(p): string => (p.autoWidth ? 'width: auto;' : '')}
`;

/* pathLength="100" keeps the dash keyframes radius-independent. */
const Arc = styled.circle`
  transform-box: fill-box;
  transform-origin: center;
  animation:
    ${spin} var(--nsknox-loader-speed, 1.4s) linear infinite,
    ${dash} calc(var(--nsknox-loader-speed, 1.4s) * 1.75) ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    stroke-dasharray: 71 100;
    stroke-dashoffset: -28;
  }
`;
